/**
 * TokenManager Component
 *
 * Handles manual token input for testing purposes.
 */

import { useState } from "react";

interface TokenManagerProps {
  onTokenSet: (token: string) => void;
  currentToken: string;
}

export function TokenManager({ onTokenSet, currentToken }: TokenManagerProps) {
  const [manualToken, setManualToken] = useState("");
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<any>(null);

  const handleUseManualToken = () => {
    if (!manualToken.trim()) {
      setError({
        errorCode: "validation_error",
        message: "Please enter a valid access token",
      });
      return;
    }

    onTokenSet(manualToken.trim());
    setResult({
      message: "Manual token set successfully",
    });
    setError(null);
  };

  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>Access Token</h2>

      {/* Manual Token Input */}
      <div style={styles.subsection}>
        <h3 style={styles.subsectionTitle}>Paste Token</h3>
        <div style={styles.tokenGroup}>
          <textarea
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
            style={styles.textarea}
            placeholder="Paste your session token here&#10;(e.g., from Postman response: eyJhbGciOiJIUzI1NiIs...)"
            rows={4}
          />
          <button
            onClick={handleUseManualToken}
            style={{ ...styles.tokenButton, backgroundColor: "#8b5cf6" }}
            disabled={!manualToken.trim()}
          >
            Use This Token
          </button>
        </div>
        <p style={styles.hint}>
          Paste the session token from Postman or your API client
        </p>
      </div>

      {/* Current Token Display */}
      {currentToken && (
        <div style={styles.currentToken}>
          <strong>Current Token:</strong>
          <div style={styles.tokenDisplay}>
            {currentToken.substring(0, 40)}...
          </div>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div style={styles.resultBox}>
          <h3 style={styles.resultTitle}>✅ {result.message}</h3>
          {result.token_type && (
            <pre style={styles.resultCode}>
              {JSON.stringify(
                {
                  token_type: result.token_type,
                  expires_in: result.expires_in,
                  scope: result.scope,
                },
                null,
                2
              )}
            </pre>
          )}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={styles.errorBox}>
          <h3 style={styles.errorTitle}>❌ Error</h3>
          <pre style={styles.errorCode}>{JSON.stringify(error, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    backgroundColor: "white",
    padding: "32px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    marginBottom: "24px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1a1a1a",
    marginBottom: "16px",
  },
  subsection: {
    marginBottom: "24px",
    paddingBottom: "24px",
    borderBottom: "1px solid #e0e0e0",
  },
  subsectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "12px",
  },
  textarea: {
    width: "100%",
    padding: "14px 16px",
    fontSize: "14px",
    border: "2px solid #e0e0e0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
    fontFamily: "monospace",
    resize: "vertical",
    minHeight: "80px",
  },
  tokenGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "8px",
  },
  tokenButton: {
    padding: "14px 24px",
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#10b981",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    whiteSpace: "nowrap",
    alignSelf: "flex-start",
  },
  currentToken: {
    marginTop: "16px",
    padding: "12px",
    backgroundColor: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "8px",
    fontSize: "14px",
  },
  tokenDisplay: {
    marginTop: "8px",
    padding: "8px",
    backgroundColor: "white",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    fontFamily: "monospace",
    fontSize: "12px",
    wordBreak: "break-all",
  },
  hint: {
    fontSize: "14px",
    color: "#999",
    marginTop: "8px",
    lineHeight: "1.5",
  },
  resultBox: {
    marginTop: "16px",
    backgroundColor: "#d4edda",
    border: "1px solid #c3e6cb",
    borderRadius: "8px",
    padding: "20px",
  },
  resultTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#155724",
    marginBottom: "12px",
  },
  resultCode: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "monospace",
    lineHeight: "1.6",
    color: "#155724",
  },
  errorBox: {
    marginTop: "16px",
    backgroundColor: "#f8d7da",
    border: "1px solid #f5c6cb",
    borderRadius: "8px",
    padding: "20px",
  },
  errorTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#721c24",
    marginBottom: "12px",
  },
  errorCode: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "monospace",
    lineHeight: "1.6",
    color: "#721c24",
  },
};
